import { useEffect, useRef, useState } from "react";
import { fetchOrgConfig } from "src/api/org";

interface UseQueryTypeOptions {
  subOrgSlug: string;
  quizId: string;
  queryTypeFromConfig: string | null | undefined;
}

// Owns the chat's queryType state and the synced ref. Three effects
// previously inlined in Chat.tsx are consolidated here:
//  - quiz_id present → force "QUIZ"
//  - sub-org slug present → fetch its config and adopt its query_type
//  - keep the ref in sync with the state for use inside callbacks that
//    can't otherwise see the latest value (e.g. the captured handleMessage
//    closure)
//
// Returns suborgConfigLoaded so the caller knows when sub-org bootstrap is
// complete (used to gate the initial greeting).
export const useQueryType = ({
  subOrgSlug,
  quizId,
  queryTypeFromConfig,
}: UseQueryTypeOptions) => {
  const [queryType, setQueryType] = useState(queryTypeFromConfig);
  const [suborgConfigLoaded, setSuborgConfigLoaded] = useState(
    !(subOrgSlug && subOrgSlug.length > 0),
  );
  const queryTypeRef = useRef(queryType || queryTypeFromConfig || "");

  // quiz_id present → query type is certainly QUIZ.
  useEffect(() => {
    if (quizId) {
      setQueryType("QUIZ");
      queryTypeRef.current = "QUIZ";
    }
  }, [quizId]);

  // Fetch sub-org config to learn its query type.
  useEffect(() => {
    if (subOrgSlug && subOrgSlug.length > 0) {
      fetchOrgConfig(subOrgSlug, false)
        .then((data) => {
          if (data?.config) {
            setQueryType(data.config.query_type);
          }
          setSuborgConfigLoaded(true);
        })
        .catch((error) => {
          console.error("Error fetching suborg config:", error);
          setSuborgConfigLoaded(true);
        });
    }
  }, [subOrgSlug]);

  // Mirror queryType into the ref for synchronous reads from event handlers.
  useEffect(() => {
    if (queryType) {
      queryTypeRef.current = queryType;
    }
  }, [queryType]);

  return { queryType, queryTypeRef, suborgConfigLoaded };
};
